import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { coffeeRecipes, ingredients } from '../data/coffeeRecipes';
import { useGame } from '../context/GameContext';

const { FiX, FiClock, FiStar, FiInfo, FiBookOpen } = FiIcons;

function RecipeViewer({ isOpen, onClose, recipeId }) {
  const { state } = useGame();
  
  if (!isOpen || !recipeId || !coffeeRecipes[recipeId]) return null;
  
  const recipe = coffeeRecipes[recipeId];
  const isUnlocked = state.recipes.includes(recipeId);
  
  // Get recipe category icon and color
  const getCategoryStyle = (category) => {
    switch (category) {
      case 'matcha':
        return { 
          icon: '🍃', 
          gradient: 'from-green-400 to-green-600',
          bg: 'from-green-50 to-green-100',
          text: 'text-green-700'
        };
      case 'milk':
        return { 
          icon: '🥛', 
          gradient: 'from-blue-400 to-blue-600',
          bg: 'from-blue-50 to-blue-100',
          text: 'text-blue-700'
        };
      case 'espresso':
        return { 
          icon: '☕', 
          gradient: 'from-amber-600 to-amber-800',
          bg: 'from-amber-50 to-amber-100',
          text: 'text-amber-700'
        };
      case 'specialty':
        return { 
          icon: '✨', 
          gradient: 'from-purple-500 to-purple-700',
          bg: 'from-purple-50 to-purple-100',
          text: 'text-purple-700'
        };
      case 'iced':
        return { 
          icon: '🧊', 
          gradient: 'from-cyan-400 to-cyan-600',
          bg: 'from-cyan-50 to-cyan-100',
          text: 'text-cyan-700'
        };
      default:
        return { 
          icon: '☕', 
          gradient: 'from-gray-400 to-gray-600',
          bg: 'from-gray-50 to-gray-100',
          text: 'text-gray-700'
        };
    }
  };

  const categoryStyle = getCategoryStyle(recipe.category);
  
  // Get difficulty stars
  const getDifficultyStars = (difficulty) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < difficulty ? 'text-yellow-400' : 'text-gray-300'}>
        ⭐
      </span>
    ));
  };

  // Calculate total preparation time
  const getTotalTime = () => {
    if (!recipe.timing) return 'Variable';
    const total = recipe.timing.reduce((acc, time) => acc + time, 0);
    return `${Math.ceil(total / 1000)}s`;
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.8, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${categoryStyle.gradient} p-4 text-white relative`}>
            <motion.button
              className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
            >
              <SafeIcon icon={FiX} className="text-white text-lg" />
            </motion.button>
            
            <div className="flex items-center mb-2">
              <span className="text-3xl mr-3">{categoryStyle.icon}</span>
              <div>
                <h2 className="text-xl font-bold">{recipe.name}</h2>
                <p className="text-white/80 text-sm capitalize">{recipe.category} • Level {recipe.difficulty}</p>
              </div>
            </div>
            
            {!isUnlocked && (
              <div className="bg-red-500/20 border border-red-300/30 rounded-lg p-2 mt-3">
                <p className="text-sm text-red-100">🔒 Recipe not unlocked yet</p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {/* Description */}
            <div className="mb-4">
              <p className="text-gray-600 leading-relaxed">{recipe.description}</p>
            </div>

            {/* Recipe Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className={`bg-gradient-to-r ${categoryStyle.bg} p-3 rounded-xl text-center`}>
                <div className="flex items-center justify-center mb-1">
                  <SafeIcon icon={FiStar} className={`${categoryStyle.text} mr-1`} />
                  <span className="text-sm font-medium text-gray-600">Difficulty</span>
                </div>
                <div className="flex justify-center">
                  {getDifficultyStars(recipe.difficulty)}
                </div>
              </div>
              
              <div className={`bg-gradient-to-r ${categoryStyle.bg} p-3 rounded-xl text-center`}>
                <div className="flex items-center justify-center mb-1">
                  <SafeIcon icon={FiClock} className={`${categoryStyle.text} mr-1`} />
                  <span className="text-sm font-medium text-gray-600">Time</span>
                </div>
                <p className={`text-lg font-bold ${categoryStyle.text}`}>
                  {getTotalTime()}
                </p>
              </div>
            </div>

            {/* Recipe Steps */}
            {isUnlocked ? (
              <div className="space-y-4">
                <div className="flex items-center mb-3">
                  <SafeIcon icon={FiBookOpen} className="text-amber-600 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">Recipe Steps</h3>
                </div>
                
                <div className="space-y-3">
                  {recipe.sequence.map((step, index) => {
                    const ingredient = ingredients[step];
                    const stepTime = recipe.timing ? recipe.timing[index] : null;
                    
                    return (
                      <motion.div
                        key={index}
                        className="flex items-center p-3 bg-gray-50 rounded-xl"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="w-8 h-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {index + 1}
                        </div>
                        
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {ingredient?.name || step.replace('_', ' ')}
                          </p>
                          {stepTime && (
                            <p className="text-xs text-gray-500">
                              ~{Math.ceil(stepTime / 1000)}s
                            </p>
                          )}
                        </div>
                        
                        {/* Step icon based on ingredient type */}
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                          {step.includes('espresso') && <span className="text-lg">☕</span>}
                          {step.includes('milk') && <span className="text-lg">🥛</span>}
                          {step.includes('foam') && <span className="text-lg">🫧</span>}
                          {step.includes('matcha') && <span className="text-lg">🍃</span>}
                          {step.includes('whisk') && <span className="text-lg">🥄</span>}
                          {step.includes('water') && <span className="text-lg">💧</span>}
                          {step.includes('ice') && <span className="text-lg">🧊</span>}
                          {step.includes('chocolate') && <span className="text-lg">🍫</span>}
                          {step.includes('cream') && <span className="text-lg">🍦</span>}
                          {!step.includes('espresso') && !step.includes('milk') && !step.includes('foam') && 
                           !step.includes('matcha') && !step.includes('whisk') && !step.includes('water') && 
                           !step.includes('ice') && !step.includes('chocolate') && !step.includes('cream') && (
                            <span className="text-lg">✨</span>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pro Tips */}
                {recipe.tips && (
                  <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-xl">
                    <div className="flex items-center mb-2">
                      <SafeIcon icon={FiInfo} className="text-yellow-600 mr-2" />
                      <h4 className="font-bold text-yellow-800">Pro Tip</h4>
                    </div>
                    <p className="text-yellow-700 text-sm leading-relaxed">{recipe.tips}</p>
                  </div>
                )}

                {/* Equipment Required */}
                <div className="mt-4">
                  <h4 className="font-bold text-gray-800 mb-2">Equipment Needed:</h4>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(recipe.sequence.map(step => ingredients[step]?.equipment).filter(Boolean))].map((equipment, index) => (
                      <span 
                        key={index}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {equipment?.replace('_', ' ').replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🔒</span>
                </div>
                <h3 className="text-lg font-bold text-gray-400 mb-2">Recipe Locked</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Unlock this recipe in the shop to view the complete preparation steps.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-sm">
                    💡 Available at Level {recipe.difficulty} in the Coffee Shop
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t">
            <motion.button
              className="w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 rounded-xl font-bold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
            >
              Close Recipe
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default RecipeViewer;